"use client";

import React, { useState, useCallback } from "react";
import { Button } from "../../button.js";
import { Input } from "../../input.js";
import { Label } from "../../label.js";
import { Badge } from "../../badge.js";
import { X, Plus, Mail, User } from "lucide-react";

export interface AttendeeItem {
  email: string;
}

interface EnhancedAttendeeFieldsProps {
  values: { attendees: AttendeeItem[] };
  touched?: Record<string, unknown>;
  errors?: Record<string, unknown>;
  setFieldValue: (field: string, value: unknown) => void;
  quickAddEmails?: string[];
}

const EnhancedAttendeeFields = ({
  values,
  touched,
  errors,
  setFieldValue,
  quickAddEmails = [],
}: EnhancedAttendeeFieldsProps) => {
  const [bulkEmails, setBulkEmails] = useState("");

  const attendees = values.attendees ?? [];
  const setAttendees = useCallback(
    (next: AttendeeItem[]) => setFieldValue("attendees", next),
    [setFieldValue]
  );

  const addBulkEmails = useCallback(() => {
    if (!bulkEmails.trim()) return;
    const emails = bulkEmails
      .split(/[,\n;]/)
      .map((email) => email.trim())
      .filter((email) => email && email.includes("@"));
    const next = [...attendees];
    emails.forEach((email) => {
      if (!next.some((a) => a.email === email)) {
        next.push({ email });
      }
    });
    setAttendees(next);
    setBulkEmails("");
  }, [bulkEmails, attendees, setAttendees]);

  const handleBulkEmailKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) {
      e.preventDefault();
      addBulkEmails();
    }
  };

  const getError = (index: number): string | undefined => {
    const a = errors?.attendees as Record<number, { email?: string }> | undefined;
    return a?.[index]?.email;
  };

  const hasError = (index: number) => !!getError(index);

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <Label className="text-base font-medium">Attendees</Label>
        <div className="text-sm text-muted-foreground">
          {attendees.length} attendee{attendees.length !== 1 ? "s" : ""}
        </div>
      </div>

      <div className="space-y-2">
        <Label className="text-sm text-muted-foreground">
          Quick Add Multiple Emails
        </Label>
        <div className="flex gap-2">
          <Input
            value={bulkEmails}
            onChange={(e) => setBulkEmails(e.target.value)}
            onKeyDown={handleBulkEmailKeyDown}
            placeholder="Enter emails separated by commas, semicolons, or new lines..."
            className="flex-1"
          />
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={addBulkEmails}
            disabled={!bulkEmails.trim()}
          >
            <Plus className="h-4 w-4" />
          </Button>
        </div>
        <p className="text-xs text-muted-foreground">
          Tip: Press Cmd/Ctrl + Enter to quickly add emails
        </p>
      </div>

      <div className="space-y-3">
        <Label className="text-sm text-muted-foreground">
          Individual Attendees
        </Label>
        <div className="space-y-3">
          {attendees.map((attendee, index) => (
            <div
              key={index}
              className="flex items-center space-x-2 p-3 border rounded-lg bg-card-50/50"
            >
              <div className="flex-1">
                <Input
                  value={attendee.email}
                  onChange={(e) => {
                    const next = attendees.map((a, i) =>
                      i === index ? { ...a, email: e.target.value } : a
                    );
                    setAttendees(next);
                  }}
                  placeholder="Enter attendee email address"
                  className={hasError(index) ? "border-red-500" : ""}
                  type="email"
                />
                {getError(index) && (
                  <p className="text-sm text-red-500 mt-1">{getError(index)}</p>
                )}
              </div>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => setAttendees(attendees.filter((_, i) => i !== index))}
                className="text-red-500 hover:text-red-700 hover:bg-red-50"
              >
                <X className="h-4 w-4" />
              </Button>
            </div>
          ))}
        </div>
      </div>

      <Button
        type="button"
        variant="outline"
        onClick={() => setAttendees([...attendees, { email: "" }])}
        className="w-full"
      >
        <Plus className="h-4 w-4 mr-2" />
        Add Individual Attendee
      </Button>

      {quickAddEmails.length > 0 && (
        <div className="space-y-2">
          <Label className="text-sm text-muted-foreground">
            Quick Add Common Emails
          </Label>
          <div className="flex flex-wrap gap-2">
            {quickAddEmails.map((email) => {
              const exists = attendees.some((a) => a.email === email);
              return (
                <Button
                  key={email}
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    if (!exists) setAttendees([...attendees, { email }]);
                  }}
                  disabled={exists}
                  className="text-xs"
                >
                  <User className="h-3 w-3 mr-1" />
                  {email.split("@")[0]}
                  {exists && <X className="h-3 w-3 ml-1" />}
                </Button>
              );
            })}
          </div>
        </div>
      )}

      {attendees.length > 0 && (
        <div className="space-y-2">
          <Label className="text-sm text-muted-foreground">
            Current Attendees
          </Label>
          <div className="flex flex-wrap gap-2 p-3 bg-blue-50 rounded-lg border border-blue-200">
            {attendees.map((attendee, index) => (
              <Badge
                key={index}
                variant="secondary"
                className="flex items-center gap-1"
              >
                <Mail className="h-3 w-3" />
                {attendee.email}
                <button
                  type="button"
                  onClick={() => setAttendees(attendees.filter((_, i) => i !== index))}
                  className="ml-1 hover:text-red-500 transition-colors"
                >
                  <X className="h-3 w-3" />
                </button>
              </Badge>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default EnhancedAttendeeFields;
